import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SideMenuLayout } from '../../lib/sharedUi/SideMenuLayout';
import SideNav from './side-nav';
import { Icon } from '../common/icon';
import { DASHBOARD } from './nav-model';
import { useNav } from './use-nav';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { logout } from '../../features/auth/authSlice';
import { authService } from '../../services/auth.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import FeedbackFab from '../../pages/feedback/feedback-fab';
import RoleSelectionGate from '../auth/role-selection-gate';
import RoleSwitcher from './role-switcher';
import UnitSwitcher from './unit-switcher';
import NotificationBell from '../notifications/notification-bell';
import { getNotificationContactId } from '../../utils/current-user';
import { projectsService } from '../../services/projects.service';
// Constant brand mark shown in the footer, same for every project.
// Drop the real 4SightAI PNG at src/assets/4sightai-logo.png.
import fourSightLogo from '../../assets/4sightai-logo.png';

const APP_NAME = '4sCoreASTRUM';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Authenticated user (for the header profile menu)
  const authUser = useSelector((state: any) => state.auth?.user);
  const userName = authUser?.name || authUser?.fullName || authUser?.email || 'User';
  // Recipient id for the notification bell. Notification documents are keyed by personnel_master._id.
  const bellContactId = getNotificationContactId(authUser);
  const bellToken = localStorage.getItem('accessToken') || '';
  const userInitial = String(userName).charAt(0).toUpperCase();

  // Per-project header branding: the logged-in project's logo + name (from
  // project_master, resolved server-side from the token's appId). When the project
  // has no logo, we show a generic mark + the project name.
  const { data: branding } = useQuery({
    // Keyed by appId so a different project (re-login / switch) gets its own branding.
    queryKey: ['current-branding', localStorage.getItem('appId') || ''],
    queryFn: () => projectsService.getCurrentBranding(),
    staleTime: 5 * 60 * 1000,
  });
  const brandName = branding?.name || APP_NAME;
  const [logoBroken, setLogoBroken] = useState(false);
  // A logo SAS URL can expire; reset the broken flag whenever the URL changes.
  useEffect(() => { setLogoBroken(false); }, [branding?.logoUrl]);
  const showProjectLogo = !!branding?.logoUrl && !logoBroken;

  const goToProfile = useCallback(() => {
    setUserMenuOpen(false);
    navigate('/profile');
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    setUserMenuOpen(false);
    // Best-effort: tell the BE so the session is revoked AND the USER.LOGOUT audit fires.
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch {
        /* ignore — proceed with local logout regardless */
      }
    }
    // Hard-clear all cached data so nothing leaks into the next session:
    // Redux permissions/user (via logout), React Query caches, and the
    // module-level personnel lookup cache.
    dispatch(logout());
    queryClient.clear();
    personnelLookupService.clearCache();
    navigate('/login');
  }, [dispatch, navigate]);

  // Scroll to top on route change
  useEffect(() => {
    const scrollableContent = document.querySelector('[class*="scrollableContent"]');
    if (scrollableContent) {
      scrollableContent.scrollTop = 0;
    }
  }, [location.pathname]);

  // The sidebar tree: permission-filtered and in the user's saved order. The
  // permissions selectors that used to sit here moved into the hook with the
  // logic that reads them.
  const { nav } = useNav();
  // Same identity the saved arrangement is keyed on, so the remembered fold
  // state is scoped to the account too rather than to the browser.
  const navUserId = authUser?._id || authUser?.id || authUser?.email || '';

  // Get current route for active state
  const currentRoute = location.pathname;
  // The tree marks a tab-level item active, so it needs the query string too.
  const activePath = `${location.pathname}${location.search}`;
  // Full-bleed screens (the chat workspace + a single conversation) drop the nav rail
  // + footer and run edge-to-edge under the top bar. Crucially this skips the scrollable
  // content pane, so the chat fills the viewport and ONLY its thread scrolls — no pane
  // or browser scroll. `/conversations/:id` matches; `/conversations` (list) does not.
  const isFullBleed = currentRoute === '/chat'
    || currentRoute.startsWith('/chat/')
    || /^\/conversations\/[^/]+$/.test(currentRoute);

  // Content container style
  const contentContainerStyle = {
    width: '100%',
    padding: '0.5rem 1rem',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
  };

  return (
    <RoleSelectionGate>
    <div className="flex flex-col h-screen bg-canvas dark:bg-gray-900">
      {/* Brand top bar — holds WHITE at the left so the white-background logo blends
          straight into the bar, then sweeps into the brand colours across to the
          right. Left-side controls use dark ink (they sit on the white zone). */}
      <header className="flex items-center justify-between h-[64px] px-4 bg-brand-header shrink-0 z-30 shadow-md">
        <div className="flex items-center gap-2">
        {/* Menu toggle — only below the lg breakpoint, where the sidebar is a drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="lg:hidden p-2 -ml-1 rounded-lg text-slate-700 hover:bg-black/5 transition-colors"
          aria-label="Open menu"
        >
          <Icon name="menu" size={20} />
        </button>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2.5"
          aria-label="Home"
        >
          {/* Per-project branding: the project logo (natural aspect, no crop) and
              the project name — the name is always shown. It's kept neutral so it
              reads cleanly next to a colored logo; a generic mark stands in when the
              project has no logo. */}
          {showProjectLogo ? (
            /* No chip: the header holds WHITE at the left (see .bg-brand-header), so a
               white-background logo blends straight into the bar. Bigger, wider mark. */
            <span className="flex items-center">
              <img
                src={branding!.logoUrl!}
                alt={brandName}
                onError={() => setLogoBroken(true)}
                className="h-11 w-auto max-w-[300px] object-contain transition-transform hover:scale-[1.03]"
              />
            </span>
          ) : (
            /* No project logo: a small brand-gradient tile reads on the white zone. */
            <span className="w-9 h-9 rounded-xl bg-brand-gradient-br text-white flex items-center justify-center shadow-sm">
              <Icon name="layout-grid" size={17} />
            </span>
          )}
          {/* The logo already carries the name; the text only stands in when there's no
              logo — dark ink so it reads on the white left zone. */}
          {!showProjectLogo && (
            <span className="text-lg font-semibold tracking-tight text-slate-800 max-w-[15rem] truncate">
              {brandName}
            </span>
          )}
        </button>
        {/* Global scope: active unit (auto-hidden for single-unit users). */}
        <div className="hidden lg:flex items-center ml-2 empty:hidden [&_.p-dropdown]:bg-black/5 [&_.p-dropdown]:border-black/10 [&_.p-dropdown_.p-dropdown-label]:text-slate-700 [&_.p-dropdown_.p-dropdown-trigger]:text-slate-500">
          <UnitSwitcher />
        </div>
        </div>

        <div className="flex items-center gap-2 text-white">
          {/* Scope the white override to just the bell so the user-menu dropdown
              items (on a white panel) keep their dark text. */}
          <span className="[&>button]:!text-white/90 [&>button:hover]:!bg-white/15">
            {bellContactId && <NotificationBell contactId={bellContactId} token={bellToken} />}
          </span>
          {/* User menu (Profile / Logout) — moved out of the sidebar */}
          <div className="relative">
          <button
            type="button"
            onClick={() => setUserMenuOpen((open) => !open)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/15 transition-colors"
          >
            <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-sm font-bold shadow-sm" style={{ color: 'var(--primary-color)' }}>
              {userInitial}
            </span>
            <span className="hidden sm:block text-sm font-medium text-white max-w-[10rem] truncate">
              {userName}
            </span>
            <Icon name="chevron-down" size={15} className={`text-white/80 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {userMenuOpen && (
            <>
              {/* click-away backdrop */}
              <button
                type="button"
                aria-hidden="true"
                className="fixed inset-0 z-40 cursor-default"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="ds-card absolute right-0 mt-2 w-52 z-50 overflow-hidden py-1">
                <div className="px-3 py-2 border-b border-line">
                  <p className="text-sm font-semibold text-ink truncate">{userName}</p>
                  {authUser?.email && (
                    <p className="text-xs text-ink-muted truncate">{authUser.email}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={goToProfile}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon name="user" size={16} className="text-gray-500" /> My Profile
                </button>
                {/* Switch Role — self-hides when the user has only one role */}
                <RoleSwitcher />
                {/* One entry, not a settings panel. Navigation order and the
                    display preferences both live in /settings now; a menu that
                    grows an option per preference stops being a menu. */}
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  data-testid="ProfileMenu.Settings"
                >
                  <Icon name="settings" size={16} className="text-gray-500" /> Settings
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                >
                  <Icon name="log-out" size={16} /> Logout
                </button>
              </div>
            </>
          )}
          </div>
        </div>
      </header>


      {/* Sidebar + content */}
      <div className="flex-1 min-h-0">
        {isFullBleed ? (
          // No nav rail, no padding — the chat workspace owns the whole area.
          <div className="h-full min-h-0">{children}</div>
        ) : (
          <SideMenuLayout
            menuItems={[]}
            activeRoute={currentRoute}
            useNavLink={false}
            collapsible={true}
            defaultCollapsed={false}
            sidebarWidth={264}
            testId="CoreService.SideMenu"
            mobileOpen={mobileOpen}
            onMobileOpenChange={setMobileOpen}
            renderNav={({ collapsed, closeMobile }) => (
              <SideNav
                nav={nav}
                dashboard={DASHBOARD}
                activePath={activePath}
                collapsed={collapsed}
                userId={navUserId}
                onNavigate={(route) => {
                  navigate(route);
                  closeMobile();
                }}
                testId="CoreService.SideNav"
              />
            )}
          >
            <div style={contentContainerStyle}>
              {children}
            </div>
          </SideMenuLayout>
        )}
      </div>

      {/* Standard footer — hidden on full-bleed screens to maximise the chat area. */}
      {!isFullBleed && (
      <footer className="flex items-center justify-between h-14 px-4 text-xs text-ink-muted bg-surface border-t border-line shrink-0">
        <span className="flex-1">© {new Date().getFullYear()} {APP_NAME}</span>
        {/* Constant brand mark, centered — same for every project. */}
        <img src={fourSightLogo} alt="4SightAI" className="h-9 object-contain shrink-0" />
        <span className="flex-1 text-right">v1.0.0</span>
      </footer>
      )}

      {/* Mounted once, here, so every screen inside MainLayout gets it. It picks
          its own form from the route and hides itself where none resolves. */}
      <FeedbackFab />
    </div>
    </RoleSelectionGate>
  );
};

export default MainLayout;
